/**
 * Interactive Three.js Kitchen Room Preview
 * Supports OrbitControls (Zoom/Rotate), TransformControls (Drag/Swap), and Distance Measurement.
 */

class Kitchen3DPreview {
  constructor(containerId) {
    this.container = document.getElementById(containerId);
    if (!this.container) return;

    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.controls = null;
    this.transformControls = null;
    this.cabinetGroup = null;
    this.appliancesGroup = null;
    this.raycaster = new THREE.Raycaster();
    this.mouse = new THREE.Vector2();
    this.selectedMesh = null;
    this.isAutoRotating = false;

    // Measurement mode state
    this.isMeasuring = false;
    this.measurePoints = [];
    this.measureMarkersGroup = new THREE.Group();

    this.initThree();
    this.initLighting();
    this.createRoomShell();
    this.initInteractions();
    this.animate();
  }

  initThree() {
    const width = this.container.clientWidth;
    const height = this.container.clientHeight || 300;

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0xf8fafc);

    this.camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
    this.camera.position.set(0, 4, 8);

    this.renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: "high-performance" });
    this.renderer.setSize(width, height);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = true;
    this.container.appendChild(this.renderer.domElement);

    if (typeof THREE.OrbitControls !== 'undefined') {
      this.controls = new THREE.OrbitControls(this.camera, this.renderer.domElement);
      this.controls.enableDamping = true;
      this.controls.dampingFactor = 0.05;
      this.controls.target.set(0, 1, 0);
      this.controls.maxPolarAngle = Math.PI / 2 - 0.05;
    }

    if (typeof THREE.TransformControls !== 'undefined') {
      this.transformControls = new THREE.TransformControls(this.camera, this.renderer.domElement);
      this.transformControls.size = 0.75;
      this.transformControls.space = 'local';
      this.scene.add(this.transformControls);

      this.transformControls.addEventListener('dragging-changed', (event) => {
        if (this.controls) this.controls.enabled = !event.value;
      });
    }

    this.cabinetGroup = new THREE.Group();
    this.appliancesGroup = new THREE.Group();
    this.scene.add(this.cabinetGroup);
    this.scene.add(this.appliancesGroup);
    this.scene.add(this.measureMarkersGroup);

    window.addEventListener('resize', () => this.onWindowResize());
  }

  initLighting() {
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.7);
    this.scene.add(ambientLight);

    const dirLight = new THREE.DirectionalLight(0xffffff, 0.8);
    dirLight.position.set(5, 10, 7);
    dirLight.castShadow = true;
    this.scene.add(dirLight);
  }

  createRoomShell() {
    const floorGeo = new THREE.PlaneGeometry(10, 10);
    const floorMat = new THREE.MeshStandardMaterial({ color: 0xe2e8f0, roughness: 0.9 });
    const floor = new THREE.Mesh(floorGeo, floorMat);
    floor.rotation.x = -Math.PI / 2;
    floor.receiveShadow = true;
    this.scene.add(floor);

    const wallGeo = new THREE.PlaneGeometry(10, 5);
    const wallMat = new THREE.MeshStandardMaterial({ color: 0xf1f5f9, roughness: 1 });
    const wall = new THREE.Mesh(wallGeo, wallMat);
    wall.position.set(0, 2.5, -5);
    this.scene.add(wall);
  }

  toggleMeasureMode(active) {
    this.isMeasuring = active;
    this.measurePoints = [];
    this.clearMeasureMarkers();
    if (active && this.transformControls) {
      this.transformControls.detach();
      this.selectedMesh = null;
    }
  }

  clearMeasureMarkers() {
    while (this.measureMarkersGroup.children.length > 0) {
      this.measureMarkersGroup.remove(this.measureMarkersGroup.children[0]);
    }
  }

  initInteractions() {
    this.renderer.domElement.addEventListener('pointerdown', (event) => {
      const rect = this.renderer.domElement.getBoundingClientRect();
      this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      this.raycaster.setFromCamera(this.mouse, this.camera);
      
      // Collect all interactable cabinet meshes
      const intersects = this.raycaster.intersectObjects(this.cabinetGroup.children, true);

      // Measurement Click Handling
      if (this.isMeasuring) {
        if (intersects.length > 0) {
          const clickedPoint = intersects[0].point;
          this.measurePoints.push(clickedPoint.clone());

          // Add visual sphere marker
          const sphereGeo = new THREE.SphereGeometry(0.08, 16, 16);
          const sphereMat = new THREE.MeshBasicMaterial({ color: 0xef4444 });
          const marker = new THREE.Mesh(sphereGeo, sphereMat);
          marker.position.copy(clickedPoint);
          this.measureMarkersGroup.add(marker);

          const distDisplay = document.getElementById("measured-distance-result");

          if (this.measurePoints.length === 2) {
            // Draw measurement line
            const lineGeo = new THREE.BufferGeometry().setFromPoints(this.measurePoints);
            const lineMat = new THREE.LineBasicMaterial({ color: 0xef4444, linewidth: 3 });
            const line = new THREE.Line(lineGeo, lineMat);
            this.measureMarkersGroup.add(line);

            const distanceMeters = this.measurePoints[0].distanceTo(this.measurePoints[1]);
            const distanceMm = Math.round(distanceMeters * 1000);

            if (distDisplay) {
              distDisplay.textContent = `Distance: ${distanceMm} mm`;
            }

            this.measurePoints = [];
            this.isMeasuring = false;
          } else {
            if (distDisplay) {
              distDisplay.textContent = `Point 1 set. Now click the second unit...`;
            }
          }
        }
        return;
      }

      // Standard TransformControls selection
      if (intersects.length > 0) {
        const clickedMesh = intersects[0].object;
        if (this.transformControls) {
          this.transformControls.attach(clickedMesh);
          this.selectedMesh = clickedMesh;
          this.transformControls.setMode('translate');
        }
      } else {
        if (this.transformControls && !this.transformControls.dragging) {
          this.transformControls.detach();
          this.selectedMesh = null;
        }
      }
    });
  }

  updateScene(selectedItems, selectedAppliances) {
    if (this.transformControls) this.transformControls.detach();
    this.clearMeasureMarkers();

    while (this.cabinetGroup.children.length > 0) {
      this.cabinetGroup.remove(this.cabinetGroup.children[0]);
    }
    while (this.appliancesGroup.children.length > 0) {
      this.appliancesGroup.remove(this.appliancesGroup.children[0]);
    }

    const textureLoader = new THREE.TextureLoader();
    const unitWidth = 1.0;

    if (selectedItems && selectedItems.length > 0) {
      const totalItems = selectedItems.reduce((acc, item) => acc + item.quantity, 0);
      let startX = -((totalItems * unitWidth) / 2) + (unitWidth / 2);
      let currentX = startX;
      let currentZ = -4.5;
      let countInRow = 0;

      selectedItems.forEach((item) => {
        for (let q = 0; q < item.quantity; q++) {
          const geometry = new THREE.BoxGeometry(unitWidth, 1.2, 0.8);
          let materials = [
            new THREE.MeshStandardMaterial({ color: 0x334155 }),
            new THREE.MeshStandardMaterial({ color: 0x334155 }),
            new THREE.MeshStandardMaterial({ color: 0x475569 }),
            new THREE.MeshStandardMaterial({ color: 0x1e293b }),
            new THREE.MeshStandardMaterial({ color: 0xffffff }),
            new THREE.MeshStandardMaterial({ color: 0x334155 })
          ];

          if (item.image) {
            textureLoader.load(item.image, (texture) => {
              texture.colorSpace = THREE.SRGBColorSpace;
              materials[4] = new THREE.MeshStandardMaterial({ map: texture, roughness: 0.4 });
              mesh.material = materials;
            }, undefined, () => {});
          }

          const mesh = new THREE.Mesh(geometry, materials);
          mesh.position.set(currentX, 0.6, currentZ);
          mesh.castShadow = true;
          mesh.receiveShadow = true;
          this.cabinetGroup.add(mesh);

          currentX += unitWidth;
          countInRow++;
          if (countInRow >= 5) {
            countInRow = 0;
            currentX = startX;
            currentZ += 0.8;
          }
        }
      });
    }

    if (selectedAppliances && selectedAppliances.length > 0) {
      let appX = 2.5;
      let appZ = -4.4;
      selectedAppliances.forEach((app, idx) => {
        const appGeo = new THREE.BoxGeometry(0.8, 1.4, 0.7);
        let appMat = new THREE.MeshStandardMaterial({ color: 0x94a3b8, metalness: 0.8, roughness: 0.2 });

        if (app.image) {
          textureLoader.load(app.image, (texture) => {
            texture.colorSpace = THREE.SRGBColorSpace;
            appMesh.material = new THREE.MeshStandardMaterial({ map: texture, roughness: 0.3 });
          }, undefined, () => {});
        }

        const appMesh = new THREE.Mesh(appGeo, appMat);
        appMesh.position.set(appX + (idx * 0.9), 0.7, appZ);
        appMesh.castShadow = true;
        this.appliancesGroup.add(appMesh);
      });
    }
  }

  zoomIn() { if (this.camera) this.camera.position.z = Math.max(2, this.camera.position.z - 1); }
  zoomOut() { if (this.camera) this.camera.position.z = Math.min(15, this.camera.position.z + 1); }
  toggleRotate() {
    this.isAutoRotating = !this.isAutoRotating;
    if (this.controls) {
      this.controls.autoRotate = this.isAutoRotating;
      this.controls.autoRotateSpeed = 4.0;
    }
  }

  animate() {
    requestAnimationFrame(() => this.animate());
    if (this.controls) this.controls.update();
    if (this.renderer && this.scene && this.camera) {
      this.renderer.render(this.scene, this.camera);
    }
  }

  onWindowResize() {
    if (!this.container) return;
    const width = this.container.clientWidth;
    const height = this.container.clientHeight || 300;
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
  }
}

window.initKitchen3D = function(containerId) {
  if (typeof THREE === 'undefined') {
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js';
    script.onload = () => loadControls(containerId);
    document.head.appendChild(script);
  } else if (typeof THREE.OrbitControls === 'undefined' || typeof THREE.TransformControls === 'undefined') {
    loadControls(containerId);
  } else {
    window.kitchen3DInstance = new Kitchen3DPreview(containerId);
  }
};

function loadControls(containerId) {
  const controlsScript = document.createElement('script');
  controlsScript.src = 'https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/controls/OrbitControls.js';
  controlsScript.onload = () => {
    const transformScript = document.createElement('script');
    transformScript.src = 'https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/controls/TransformControls.js';
    transformScript.onload = () => {
      window.kitchen3DInstance = new Kitchen3DPreview(containerId);
    };
    document.head.appendChild(transformScript);
  };
  document.head.appendChild(controlsScript);
}